import {
  AssetType,
  TransformationVariant,
  DeviceFamily,
  Placement,
} from '@amzn/fire-tv-campaign-automation-service';
import { STAGE } from 'src/constants';

const VARIANT_DISPLAY_NAMES: Partial<Record<TransformationVariant, string>> = {
  [TransformationVariant.NO_TRANSFORMATION]: 'No Transformation',
  [TransformationVariant.DOMINANT_COLOR_FADED_OVERLAY]: 'Dominant Color Faded Overlay',
  [TransformationVariant.FULL_BLEED]: 'Full Bleed',
  [TransformationVariant.TEXT_BASED_TITLE_DESCRIPTION]: 'Text Based Title Description',
  [TransformationVariant.CLIPPED_VIDEO]: 'Clipped Video',
  [TransformationVariant.SVG_TO_PNG_ALL_FORMATS]: 'SVG to PNG All Formats',
  [TransformationVariant.TEST_VARIANT]: 'Test Variant',
  [TransformationVariant.TEST_VARIANT_SVG_TO_PNG]: 'Test Variant SVG to PNG',
  [TransformationVariant.DOMINANT_COLOR_FADED_OVERLAY_FIRE_TABLETS]: 'Dominant Color Faded Overlay',
  [TransformationVariant.TEXT_BASED_TITLE_FIRE_TABLETS]: 'Text Based Title',
  [TransformationVariant.TRIVIA_TRAILER]: 'Trivia Based Trailer',
  [TransformationVariant.MULTI_TITLE_HORIZONTAL]: 'Multi Title Horizontal',

  [TransformationVariant.MULTI_TITLE_VERTICAL]: 'Multi Title Vertical',
  [TransformationVariant.OVERLAY_ONLY]: 'Overlay Only',
  [TransformationVariant.GAMIFIED_TRAILER]: 'Gamified Trailer',
};

/**
 * The configuration of Transformation variants with Stage, DeviceFamily, Placement and AssetType.
 */
const TRANSFORMATION_VARIANTS: Record<
  STAGE,
  Record<
    DeviceFamily,
    Partial<Record<Placement, Partial<Record<AssetType, TransformationVariant[]>>>>
  >
> = {
  [STAGE.DEV]: {
    [DeviceFamily.FIRE_TV]: {
      [Placement.FEATURE_ROTATOR]: {
        [AssetType.BACKGROUND_IMAGE]: [
          TransformationVariant.NO_TRANSFORMATION,
          TransformationVariant.DOMINANT_COLOR_FADED_OVERLAY,
          TransformationVariant.FULL_BLEED,
          TransformationVariant.MULTI_TITLE_HORIZONTAL,

          TransformationVariant.MULTI_TITLE_VERTICAL,
        ],
        [AssetType.LOGO]: [
          TransformationVariant.TEXT_BASED_TITLE_DESCRIPTION,
          TransformationVariant.NO_TRANSFORMATION,
        ],
        [AssetType.VIDEO_TRAILER]: [
          TransformationVariant.NO_TRANSFORMATION,
          TransformationVariant.CLIPPED_VIDEO,
          TransformationVariant.TRIVIA_TRAILER,
          TransformationVariant.GAMIFIED_TRAILER,
        ],
      },
      [Placement.SCREEN_SAVER]: {
        [AssetType.BACKGROUND_IMAGE]: [
          TransformationVariant.NO_TRANSFORMATION,
          TransformationVariant.DOMINANT_COLOR_FADED_OVERLAY,
        ],
        [AssetType.LOGO]: [TransformationVariant.TEXT_BASED_TITLE_DESCRIPTION],
      },
    },
    [DeviceFamily.FIRE_TABLETS]: {
      [Placement.FEATURE_ROTATOR]: {
        [AssetType.BACKGROUND_IMAGE]: [
          TransformationVariant.NO_TRANSFORMATION,
          TransformationVariant.FULL_BLEED,
          TransformationVariant.DOMINANT_COLOR_FADED_OVERLAY_FIRE_TABLETS,
        ],
        [AssetType.LOGO]: [
          TransformationVariant.TEXT_BASED_TITLE_FIRE_TABLETS,
          TransformationVariant.NO_TRANSFORMATION,
        ],
      },
    },
  },
  [STAGE.BETA]: {
    [DeviceFamily.FIRE_TV]: {
      [Placement.FEATURE_ROTATOR]: {
        [AssetType.BACKGROUND_IMAGE]: [
          TransformationVariant.NO_TRANSFORMATION,
          TransformationVariant.DOMINANT_COLOR_FADED_OVERLAY,
          TransformationVariant.FULL_BLEED,
          TransformationVariant.MULTI_TITLE_HORIZONTAL,

          TransformationVariant.MULTI_TITLE_VERTICAL,
        ],
        [AssetType.LOGO]: [
          TransformationVariant.TEXT_BASED_TITLE_DESCRIPTION,
          TransformationVariant.NO_TRANSFORMATION,
        ],
        [AssetType.VIDEO_TRAILER]: [
          TransformationVariant.NO_TRANSFORMATION,
          TransformationVariant.CLIPPED_VIDEO,
          TransformationVariant.TRIVIA_TRAILER,
          TransformationVariant.GAMIFIED_TRAILER,
        ],
      },
      [Placement.SCREEN_SAVER]: {
        [AssetType.BACKGROUND_IMAGE]: [
          TransformationVariant.NO_TRANSFORMATION,
          TransformationVariant.DOMINANT_COLOR_FADED_OVERLAY,
        ],
        [AssetType.LOGO]: [TransformationVariant.TEXT_BASED_TITLE_DESCRIPTION],
      },
    },
    [DeviceFamily.FIRE_TABLETS]: {
      [Placement.FEATURE_ROTATOR]: {
        [AssetType.BACKGROUND_IMAGE]: [
          TransformationVariant.NO_TRANSFORMATION,
          TransformationVariant.FULL_BLEED,
          TransformationVariant.DOMINANT_COLOR_FADED_OVERLAY_FIRE_TABLETS,
        ],
        [AssetType.LOGO]: [
          TransformationVariant.TEXT_BASED_TITLE_FIRE_TABLETS,
          TransformationVariant.NO_TRANSFORMATION,
        ],
      },
    },
  },
  [STAGE.GAMMA]: {
    [DeviceFamily.FIRE_TV]: {
      [Placement.FEATURE_ROTATOR]: {
        [AssetType.BACKGROUND_IMAGE]: [
          TransformationVariant.NO_TRANSFORMATION,
          TransformationVariant.DOMINANT_COLOR_FADED_OVERLAY,
          TransformationVariant.FULL_BLEED,
          TransformationVariant.MULTI_TITLE_HORIZONTAL,

          TransformationVariant.MULTI_TITLE_VERTICAL,
        ],
        [AssetType.LOGO]: [
          TransformationVariant.TEXT_BASED_TITLE_DESCRIPTION,
          TransformationVariant.NO_TRANSFORMATION,
        ],
        [AssetType.VIDEO_TRAILER]: [
          TransformationVariant.NO_TRANSFORMATION,
          TransformationVariant.CLIPPED_VIDEO,
          TransformationVariant.TRIVIA_TRAILER,
          TransformationVariant.GAMIFIED_TRAILER,
        ],
      },
      [Placement.SCREEN_SAVER]: {
        [AssetType.BACKGROUND_IMAGE]: [
          TransformationVariant.NO_TRANSFORMATION,
          TransformationVariant.DOMINANT_COLOR_FADED_OVERLAY,
        ],
        [AssetType.LOGO]: [TransformationVariant.TEXT_BASED_TITLE_DESCRIPTION],
      },
    },
    [DeviceFamily.FIRE_TABLETS]: {
      [Placement.FEATURE_ROTATOR]: {
        [AssetType.BACKGROUND_IMAGE]: [
          TransformationVariant.NO_TRANSFORMATION,
          TransformationVariant.FULL_BLEED,
          TransformationVariant.DOMINANT_COLOR_FADED_OVERLAY_FIRE_TABLETS,
        ],
        [AssetType.LOGO]: [
          TransformationVariant.TEXT_BASED_TITLE_FIRE_TABLETS,
          TransformationVariant.NO_TRANSFORMATION,
        ],
      },
    },
  },
  [STAGE.PROD_NA]: {
    [DeviceFamily.FIRE_TV]: {
      [Placement.FEATURE_ROTATOR]: {
        [AssetType.BACKGROUND_IMAGE]: [
          TransformationVariant.NO_TRANSFORMATION,
          TransformationVariant.DOMINANT_COLOR_FADED_OVERLAY,
          TransformationVariant.FULL_BLEED,
        ],
        [AssetType.LOGO]: [TransformationVariant.TEXT_BASED_TITLE_DESCRIPTION],
        [AssetType.VIDEO_TRAILER]: [
          TransformationVariant.NO_TRANSFORMATION,
          TransformationVariant.CLIPPED_VIDEO,
        ],
      },
      [Placement.SCREEN_SAVER]: {
        [AssetType.BACKGROUND_IMAGE]: [
          TransformationVariant.NO_TRANSFORMATION,
          TransformationVariant.DOMINANT_COLOR_FADED_OVERLAY,
        ],
        [AssetType.LOGO]: [TransformationVariant.TEXT_BASED_TITLE_DESCRIPTION],
      },
    },
    [DeviceFamily.FIRE_TABLETS]: {
      [Placement.FEATURE_ROTATOR]: {
        [AssetType.BACKGROUND_IMAGE]: [
          TransformationVariant.NO_TRANSFORMATION,
          TransformationVariant.FULL_BLEED,
          TransformationVariant.DOMINANT_COLOR_FADED_OVERLAY_FIRE_TABLETS,
        ],
        [AssetType.LOGO]: [TransformationVariant.TEXT_BASED_TITLE_FIRE_TABLETS],
      },
    },
  },
};

export function getVariantsFromConfig(
  stage: STAGE,
  deviceFamily: DeviceFamily,
  placement: Placement,
) {
  const result: Partial<Record<AssetType, Array<{ value: string; name: string }>>> = {};

  const variants = TRANSFORMATION_VARIANTS[stage]?.[deviceFamily]?.[placement];
  if (!variants) return result;

  Object.entries(variants).forEach(([assetType, transformationVariants]) => {
    if (transformationVariants && transformationVariants.length > 0) {
      result[assetType as AssetType] = transformationVariants.map((variant) => ({
        value: variant,
        name: VARIANT_DISPLAY_NAMES[variant] ?? variant,
      }));
    }
  });

  return result;
}
